import { createPortfolio, getPortfolios } from "@/pages/PortfolioBuilder/api";
import { emptyNotifications } from "@/types/notifications";
import type { Portfolio } from "@/types/Portfolio";
import { makeNameClaimer } from "./concierge/claimName";
import { resolveTerms } from "@/shared/utils/resolveTerms";
import type { TemplateWidgetSpec } from "./templates/types";

const DEFAULT_NAME = "My Portfolio";

/** Section the dedicated instance filters on — the ticker-watchlist filter. */
const FILTER_SECTION = "ticker-watchlists-filter";

export type OwnPortfolioResult = {
  id: number;
  name: string;
  /**
   * A headlines instance narrowed to just these names, to append to the tab
   * being built. Separate from the primary wire, which only ever highlights.
   */
  widget: TemplateWidgetSpec;
};

/**
 * Turns the tickers typed on step 1 into a real watchlist.
 *
 * Shared by both routes: the template path builds it before applying a layout,
 * and the concierge path creates it before the plan so the LLM's own lists sit
 * alongside it rather than duplicating it.
 *
 * Named through `makeNameClaimer` against the user's existing lists, so
 * someone onboarding a second time — or who already has a "My Portfolio" —
 * gets "My Portfolio 2" rather than a second indistinguishable row in the
 * filter panel.
 *
 * Returns null when there is nothing to make, so callers can treat "no
 * tickers" and "creation failed" the same way: carry on without it. The
 * dashboard is the thing the user asked for and must not fail over this.
 */
export async function createOwnPortfolio(
  tickers: string[]
): Promise<OwnPortfolioResult | null> {
  const entered = tickers.map((t) => t.trim()).filter(Boolean);
  if (entered.length === 0) return null;

  try {
    /**
     * Same resolution the watchlist builder does, so "Apple" becomes AAPL
     * rather than a ticker literally called APPLE.
     *
     * Run here, once, rather than per keystroke in the field: onboarding
     * should not make a network call while someone is still typing their
     * book, and the answer is only needed when the watchlist is created.
     *
     * Falls back to the raw input if the tag service is unreachable — a
     * watchlist of plain strings is worth much more than no watchlist.
     */
    const { resolved } = await resolveTerms(entered).catch(() => ({
      resolved: [],
    }));

    const symbols: string[] = [];
    const keywords: string[] = [];
    const tags: string[] = [];
    for (const term of resolved) {
      if (term.ticker) symbols.push(term.ticker);
      else if (term.keyword) keywords.push(term.keyword);
      if (term.tagId != null) tags.push(String(term.tagId));
    }
    // Nothing resolved (offline, or the service is down) — keep what they
    // typed rather than silently creating an empty list.
    const clean = symbols.length || keywords.length
      ? symbols
      : entered.map((t) => t.toUpperCase());
    const existing = await getPortfolios().catch(() => [] as Portfolio[]);
    const claim = makeNameClaimer(existing.map((p) => p.name));
    const name = claim(DEFAULT_NAME);
    // The server allocates the id and ignores the one sent, so the widget
    // filter below has to use the id that comes back — the one sent pointed
    // the new panel at some other list, or at none.
    const { id } = await createPortfolio({
      id: 0,
      name,
      tickers: clean,
      keywords,
      tags,
      color: "#00AEEF",
      priority: 1,
      notifications: emptyNotifications(),
      customizations: { includeRelatedHeadlines: false },
      owner_type: "USER",
    });

    return {
      id,
      name,
      widget: {
        id: "NewsquawkHeadlines-mine",
        size: { w: 5, h: 5 },
        filter: { [FILTER_SECTION]: [String(id)] },
      },
    };
  } catch (error) {
    console.error("[onboarding] failed to create own portfolio:", error);
    return null;
  }
}
